// src/pages/ConsumoAguaPotable.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, doc, getDoc, updateDoc, setDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useModulos } from '../hooks/useModulos';
import {
  FaArrowLeft,
  FaFileExcel,
  FaUpload,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaInfoCircle,
  FaWater,
  FaTint,
  FaDownload,
  FaHistory,
  FaPercentage,
  FaQuestionCircle,
  FaShieldAlt,
  FaFilePdf
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import { parseAguaExcel, descargarPlantillaAgua, parseAguaPdf } from '../utils/aguaExcelParser';
import * as pdfjsLib from 'pdfjs-dist';
import './ConsumoAguaPotable.css';

// Configurar el worker de PDF.js desde la carpeta public
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const fmtMXN = (val) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val || 0);

const fmtMXNEntero = (val) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Math.round(val || 0));

const ConsumoAguaPotable = () => {
  const { userData } = useAuth();
  const fraccionamientoId = useMemo(() => {
    return (
      userData?.fraccionamientoId ||
      userData?.fraccionamientoTd ||
      userData?.fraccionamientoSeleccionadoId ||
      userData?.fraccionamientoSeleccionado ||
      localStorage.getItem('selectedFraccionamientoId') ||
      localStorage.getItem('fraccionamientoId') ||
      ''
    );
  }, [userData]);
  const navigate = useNavigate();
  const { modulos } = useModulos(fraccionamientoId);

  // Estados de datos
  const [casas, setCasas] = useState([]);
  const [historialConsumos, setHistorialConsumos] = useState([]);
  const [selectedPeriodoId, setSelectedPeriodoId] = useState(null);

  // Estados de carga de archivo y previsualización
  const [fileLoading, setFileLoading] = useState(false);
  const [fileData, setFileData] = useState(null); // Objeto parseado por aguaExcelParser
  const [erroresLectura, setErroresLectura] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentPageTable, setCurrentPageTable] = useState(1);
  const [fraccConfig, setFraccConfig] = useState(null);

  // Cargar casas, consumos y configuración del fraccionamiento de Firestore
  useEffect(() => {
    if (!fraccionamientoId) return;

    // Escuchar documento del fraccionamiento para configuraciones (recibos, logo, nombre)
    const unsubFracc = onSnapshot(doc(db, 'fraccionamientos', fraccionamientoId), (snap) => {
      if (snap.exists()) {
        setFraccConfig(snap.data());
      }
    });

    // Escuchar colección de casas
    const unsubCasas = onSnapshot(
      collection(db, 'fraccionamientos', fraccionamientoId, 'casas'),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setCasas(list);
      }
    );

    // Escuchar colección de consumos de agua
    const unsubConsumos = onSnapshot(
      collection(db, 'fraccionamientos', fraccionamientoId, 'consumosAguaPotable'),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Ordenar por fecha de creación descendente
        list.sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
        setHistorialConsumos(list);
        if (list.length > 0 && !selectedPeriodoId) {
          setSelectedPeriodoId(list[0].id);
        }
      }
    );

    return () => {
      unsubFracc();
      unsubCasas();
      unsubConsumos();
    };
  }, [fraccionamientoId, selectedPeriodoId]);

  // Selección del período actual para visualizar reporte
  const periodoSeleccionado = useMemo(() => {
    if (!selectedPeriodoId) return historialConsumos[0] || null;
    return historialConsumos.find((item) => item.id === selectedPeriodoId) || null;
  }, [historialConsumos, selectedPeriodoId]);

  // Calcular porcentaje de cobro del período seleccionado
  const metricasPeriodo = useMemo(() => {
    if (!periodoSeleccionado || !periodoSeleccionado.desgloseCasas) {
      return { totalCasas: 0, pagadas: 0, pendientes: 0, porcentajePagado: 0, totalRecaudado: 0, totalCEA: 0 };
    }
    const desglose = periodoSeleccionado.desgloseCasas || [];
    const totalCasas = desglose.length;
    let pagadas = 0;
    let totalRecaudado = 0;

    desglose.forEach((item) => {
      if (item.estadoPago === 'PAGADO' || item.pagado === true) {
        pagadas++;
        totalRecaudado += Number(item.cargoTotal || 0);
      }
    });

    const pendientes = totalCasas - pagadas;
    const porcentajePagado = totalCasas > 0 ? Math.round((pagadas / totalCasas) * 100) : 0;

    return {
      totalCasas,
      pagadas,
      pendientes,
      porcentajePagado,
      totalRecaudado,
      totalCEA: Number(periodoSeleccionado.totalReciboCEA || 0)
    };
  }, [periodoSeleccionado]);

  // Manejador de la subida del archivo Excel
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setFileLoading(true);
      setErroresLectura([]);
      const isPdf = file.name.toLowerCase().endsWith('.pdf');
      let parsed;

      if (isPdf) {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        parsed = await parseAguaPdf(pdf);
      } else {
        const arrayBuffer = await file.arrayBuffer();
        parsed = await parseAguaExcel(arrayBuffer);
      }

      // Validar casas existentes y lecturas
      const errores = [...(parsed.erroresFormato || [])];

      const normalizeHouseNumExact = (val) => {
        if (val === null || val === undefined) return '';
        return String(val).trim().toUpperCase().replace(/^CASA\s*[-:_]?\s*/i, '');
      };

      const findMatchingCasa = (excelCasaNum) => {
        const normExcel = normalizeHouseNumExact(excelCasaNum);
        if (!normExcel) return null;

        return casas.find((c) => {
          const props = [c.numero, c.numeroCasa, c.casa, c.lote, c.id];
          return props.some((p) => {
            if (!p) return false;
            const normP = normalizeHouseNumExact(p);
            if (normP === normExcel) return true;
            // Solo quitar espacios y guiones (ej. W-999 == W999), pero PRESERVAR letras (A-333 jamás igual a 333)
            const stripP = normP.replace(/[\s-]/g, '');
            const stripExcel = normExcel.replace(/[\s-]/g, '');
            return stripP === stripExcel;
          });
        });
      };

      // 1. Detectar si el archivo Excel contiene casas duplicadas
      const conteoCasasExcel = new Map();
      parsed.casas.forEach((item) => {
        const normKey = normalizeHouseNumExact(item.casaNumero);
        if (normKey) {
          conteoCasasExcel.set(normKey, (conteoCasasExcel.get(normKey) || 0) + 1);
        }
      });

      conteoCasasExcel.forEach((cantidad, casaKey) => {
        if (cantidad > 1) {
          errores.push(`⚠️ Casa "${casaKey}": Aparece repetida ${cantidad} veces en el archivo cargado. Cada casa debe tener solo una fila en el archivo.`);
        }
      });

      // 2. Validar si ya existe un recibo cargado en Firestore con la misma fecha/mes del año en curso
      const anioActual = parsed.anoLectura || new Date().getFullYear();
      const periodoClave = `${parsed.mesLectura}-${anioActual}`.toUpperCase();

      const yaExisteMismaFecha = historialConsumos.some((h) => {
        const hClave = `${h.mesLectura || ''}-${h.anoLectura || ''}`.toUpperCase();
        return hClave === periodoClave || (h.periodoLectura && h.periodoLectura.toLowerCase() === parsed.periodoLectura.toLowerCase());
      });

      if (yaExisteMismaFecha) {
        errores.push(`⚠️ ATENCIÓN: Ya se encuentra registrado un recibo de agua para el período "${parsed.periodoLectura}" (${periodoClave}) del año en curso. Para evitar cobros duplicados, verifique el período del archivo.`);
      }

      // 3. Validar coincidencia de cada casa en Firestore y lecturas
      parsed.casas.forEach((item) => {
        const casaObj = findMatchingCasa(item.casaNumero);

        if (!casaObj) {
          errores.push(`Casa ${item.casaNumero}: La casa no se encuentra registrada en el sistema del fraccionamiento.`);
        }

        if (item.lecturaActual < item.lecturaAnterior) {
          errores.push(`Casa ${item.casaNumero}: La lectura actual (${item.lecturaActual} m³) no puede ser menor a la lectura anterior (${item.lecturaAnterior} m³).`);
        }

        if (item.cargoTotal <= 0 && item.consumoM3 > 0) {
          errores.push(`Casa ${item.casaNumero}: El cargo total es $0 pero registra un consumo de ${item.consumoM3} m³.`);
        }
      });

      setFileData(parsed);
      setErroresLectura(errores);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Error parseando archivo:', error);
      Swal.fire('Error', 'No se pudo leer el archivo. Asegúrese de que sea un archivo Excel (.xlsx, .xls) válido con la plantilla de consumos.', 'error');
    } finally {
      setFileLoading(false);
      event.target.value = '';
    }
  };

  // Confirmar procesamiento desde la Previsualización
  const handleAprobarPrevisualizacion = () => {
    if (erroresLectura.length > 0) {
      Swal.fire({
        title: 'Existen Errores en la Lectura',
        text: 'Por favor corrija los errores reportados en el archivo Excel antes de procesar.',
        icon: 'warning'
      });
      return;
    }
    setShowPreviewModal(false);
    setShowConfirmModal(true);
  };

  // Modificar Precio por m³ desde la previsualización y recalcular importes por casa
  const handleUpdatePrecioM3 = (nuevoPrecioStr) => {
    const nuevoPrecio = parseFloat(nuevoPrecioStr) || 0;
    setFileData((prev) => {
      if (!prev) return prev;
      const nuevasCasas = (prev.casas || []).map((c) => {
        const consumoPesos = Math.round(c.consumoM3 * nuevoPrecio);
        const cargoTotal = Math.round(consumoPesos + (c.prorrateoComun || 0));
        return {
          ...c,
          consumoPesos,
          cargoTotal,
        };
      });
      return {
        ...prev,
        precioM3: nuevoPrecio,
        casas: nuevasCasas,
      };
    });
  };

  // Modificar Total del Recibo desde la previsualización
  const handleUpdateTotalRecibo = (nuevoTotalStr) => {
    const nuevoTotal = parseFloat(nuevoTotalStr) || 0;
    setFileData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        totalReciboCEA: nuevoTotal,
      };
    });
  };

  // Modificar Período de Lectura desde la previsualización
  const handleUpdatePeriodo = (nuevoPeriodo) => {
    setFileData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        periodoLectura: nuevoPeriodo,
      };
    });
  };

  // Guardar consumos y opcionalmente asignar al historial de pagos
  const ejecutarGuardadoConsumos = async (asignarHistorial) => {
    if (!fileData || !fraccionamientoId) return;

    try {
      setIsProcessing(true);
      setShowConfirmModal(false);

      const anioLectura = fileData.anoLectura || new Date().getFullYear();
      const mesLectura = fileData.mesLectura || 'ACTUAL';
      const periodoClave = `${mesLectura}-${anioLectura}`.toUpperCase();
      const docId = `AGUA-${periodoClave}-${Date.now().toString().slice(-4)}`;

      const desgloseProcesado = fileData.casas.map((item) => {
        const normItem = String(item.casaNumero).trim().toUpperCase().replace(/^CASA\s*[-:_]?\s*/i, '');
        const casaObj = casas.find((c) => {
          const props = [c.numero, c.numeroCasa, c.casa, c.lote, c.id];
          return props.some((p) => {
            if (!p) return false;
            const normP = String(p).trim().toUpperCase().replace(/^CASA\s*[-:_]?\s*/i, '');
            if (normP === normItem) return true;
            const stripP = normP.replace(/[\s-]/g, '');
            const stripItem = normItem.replace(/[\s-]/g, '');
            return stripP === stripItem;
          });
        });

        return {
          ...item,
          casaId: casaObj ? casaObj.id : `Casa-${item.casaNumero}`,
          pagado: false,
          estadoPago: 'PENDIENTE',
          montoPendiente: item.cargoTotal
        };
      });

      const nuevoConsumoDoc = {
        id: docId,
        fraccionamientoId,
        totalReciboCEA: fileData.totalReciboCEA,
        totalM3Fracc: fileData.totalM3Fracc,
        precioM3: fileData.precioM3,
        periodoLectura: fileData.periodoLectura,
        mesLectura,
        anoLectura: anioLectura,
        desgloseCasas: desgloseProcesado,
        cargadoEnHistorial: asignarHistorial === true,
        fechaAsignacionHistorial: asignarHistorial ? new Date().toISOString() : null,
        createdAt: Timestamp.now(),
        creadoPor: userData?.email || 'Administrador'
      };

      // 1. Si seleccionó asignación al historial, procesar cargos y actualizar el desglose con los pagos
      if (asignarHistorial) {
        await aplicarCargosAHistorialResidentes(desgloseProcesado, fileData.periodoLectura, mesLectura, anioLectura);
        nuevoConsumoDoc.desgloseCasas = desgloseProcesado;
      }

      // 2. Guardar el documento de consumos definitivo en Firestore
      await setDoc(doc(db, 'fraccionamientos', fraccionamientoId, 'consumosAguaPotable', docId), nuevoConsumoDoc);

      Swal.fire({
        title: '¡Consumo Procesado Con Éxito!',
        html: `El registro de consumo de agua para el período <b>${fileData.periodoLectura}</b> ha sido guardado correctamente.` +
          (asignarHistorial ? '<br><small class="text-success">✓ Se aplicaron los cargos al historial de pagos de cada residente.</small>' : '<br><small class="text-muted">ⓘ Se guardó como registro informativo sin afectar saldos.</small>') +
          '<br><br><small style="color: #0369a1; font-weight: 600;">📧 Se envió un respaldo en PDF con los saldos vigentes por vivienda al correo domushabitat2025@gmail.com.</small>',
        icon: 'success'
      });

      setSelectedPeriodoId(docId);
      setFileData(null);
    } catch (err) {
      console.error('Error al guardar consumos de agua:', err);
      const isPermissionErr = String(err.message || '').toLowerCase().includes('permission');
      const msg = isPermissionErr
        ? 'No cuentas con los permisos de administración necesarios en Firestore para registrar este consumo de agua o actualizar los historiales del fraccionamiento.'
        : (err.message || 'Ocurrió un error al guardar los consumos.');

      Swal.fire('Error al Procesar', msg, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Manejar respuesta SÍ / NO en la confirmación de asignación a historial
  const handleRespuestaConfirmacion = (respuestaSi) => {
    if (respuestaSi) {
      ejecutarGuardadoConsumos(true);
    } else {
      // Reconfirmar si está seguro de NO asignarlo al historial
      Swal.fire({
        title: '¿Está seguro de NO marcarlo en el historial?',
        text: 'La lectura se guardará solo como información de consulta para los administradores. No se sumará al adeudo ni se descontará de los residentes en esta ocasión.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, solo guardar como información',
        cancelButtonText: 'Regresar',
        confirmButtonColor: '#6c757d',
        cancelButtonColor: '#3085d6'
      }).then((result) => {
        if (result.isConfirmed) {
          ejecutarGuardadoConsumos(false);
        } else {
          setShowConfirmModal(true);
        }
      });
    }
  };

  // Helper para formatear periodo de lectura
  const formatPeriodoLectura = (periodoStr) => {
    const str = String(periodoStr || '').toLowerCase();
    const meses = {
      'enero': { num: '01', name: 'Enero' },
      'febrero': { num: '02', name: 'Febrero' },
      'marzo': { num: '03', name: 'Marzo' },
      'abril': { num: '04', name: 'Abril' },
      'mayo': { num: '05', name: 'Mayo' },
      'junio': { num: '06', name: 'Junio' },
      'julio': { num: '07', name: 'Julio' },
      'agosto': { num: '08', name: 'Agosto' },
      'septiembre': { num: '09', name: 'Septiembre' },
      'octubre': { num: '10', name: 'Octubre' },
      'noviembre': { num: '11', name: 'Noviembre' },
      'diciembre': { num: '12', name: 'Diciembre' }
    };
    
    const numbers = str.match(/\d+/g) || [];
    const words = str.split(/[\s,]+/);
    const mesesEncontrados = [];
    
    for (const word of words) {
      const cleanWord = word.replace(/[^a-zñáéíóúü]/g, '');
      if (meses[cleanWord]) {
        mesesEncontrados.push(meses[cleanWord]);
      }
    }
    
    if (numbers.length >= 2 && mesesEncontrados.length >= 2) {
      const diaInicio = String(numbers[0]).padStart(2, '0');
      const mesInicio = mesesEncontrados[0].num;
      const diaFin = String(numbers[1]).padStart(2, '0');
      const mesFin = mesesEncontrados[1].num;
      
      const conceptoPeriodo = `${diaInicio}-${mesInicio} al ${diaFin}-${mesFin}`;
      const refPeriodo = `Agua-Potable-${mesesEncontrados[0].name}-${mesesEncontrados[1].name}`;
      return { conceptoPeriodo, refPeriodo };
    }
    
    const date = new Date();
    const mesActualName = Object.values(meses)[date.getMonth()].name;
    const mesAnteriorName = Object.values(meses)[(date.getMonth() - 1 + 12) % 12].name;
    return {
      conceptoPeriodo: periodoStr,
      refPeriodo: `Agua-Potable-${mesAnteriorName}-${mesActualName}`
    };
  };

  // Aplicar cargos al historial y cuota mensual de los residentes
  const aplicarCargosAHistorialResidentes = async (desglose, periodoLectura, mesLectura, anoLectura) => {
    const { conceptoPeriodo, refPeriodo } = formatPeriodoLectura(periodoLectura);

    for (const item of desglose) {
      if (!item.casaId || item.cargoTotal <= 0) continue;

      const casaRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', item.casaId);
      const casaSnap = await getDoc(casaRef);

      if (casaSnap.exists()) {
        const dataCasa = casaSnap.data();
        const adeudoActual = Number(dataCasa.adeudo || dataCasa.deudaTotal || 0);
        const saldoFavorActual = Number(dataCasa.saldoFavor || 0);
        const cargoAgua = Number(item.cargoTotal || 0);

        let nuevoAdeudo = adeudoActual + cargoAgua;
        let nuevoSaldoFavor = saldoFavorActual;
        let estadoMovimiento = 'CON ADEUDO';
        let montoPagadoAuto = 0;
        let pagadoCompleto = false;

        // Descontar automáticamente del saldo a favor si tiene fondos suficientes
        if (saldoFavorActual >= cargoAgua) {
          pagadoCompleto = true;
          montoPagadoAuto = cargoAgua;
          nuevoAdeudo = adeudoActual; // No se incrementa el adeudo
          nuevoSaldoFavor = Math.round((saldoFavorActual - cargoAgua) * 100) / 100;
          estadoMovimiento = 'PAGADO';
        }

        // Actualizar casa en Firestore
        await updateDoc(casaRef, {
          adeudo: nuevoAdeudo,
          saldoFavor: nuevoSaldoFavor,
          tieneAdeudoAguaVariable: !pagadoCompleto, // Se desmarca si se pagó completo
          ultimoMesAguaVariable: `${mesLectura}-${anoLectura}`
        });

        // Registrar en el historial y saldos con ID determinista
        const transaccionId = `AGUA-${item.casaId}-${mesLectura}-${anoLectura}`;
        const refIngresoGlobal = doc(db, 'fraccionamientos', fraccionamientoId, 'ingresos', transaccionId);

        // Fecha de transacción en objeto Timestamp de Firestore (hora exacta local de la carga)
        const fechaTimestamp = Timestamp.now();

        // Concepto y tipos condicionados según el estado de pago automático
        const conceptoTrans = pagadoCompleto
          ? `Consumo Agua Potable. ${conceptoPeriodo} pagado automático con Saldo a Favor,`
          : `Consumo Agua Potable. ${conceptoPeriodo} calculado,`;

        const tipoTrans = 'Consumo de Agua Potable M3';
        const metodoTrans = 'registro_automatico';
        const estadoTrans = pagadoCompleto ? 'PAGADO' : 'CON ADEUDO';
        const estadoPagoTrans = pagadoCompleto ? 'pagado' : 'moroso';
        const colorBanderaTrans = pagadoCompleto ? 'verde' : 'rojo';

        const observacionesTrans = pagadoCompleto
          ? 'Pago Automático con Saldo a Favor - PAGADO'
          : 'Adeudo Consumo de Agua Potable - CON ADEUDO';

        // 1. Registrar siempre el recibo de cobro de agua en ingresos global para el historial del residente
        const transData = {
          id: transaccionId,
          casaId: item.casaId,
          casaNumero: item.casaNumero,
          fecha: fechaTimestamp,
          concepto: conceptoTrans,
          tipo: tipoTrans,
          metodoPago: metodoTrans,
          referencia: refPeriodo,
          monto: cargoAgua,
          neto: pagadoCompleto ? cargoAgua : -cargoAgua,
          montoNeto: pagadoCompleto ? cargoAgua : -cargoAgua,
          descuento: 0,
          estado: estadoTrans,
          estadoResidente: estadoTrans, // Guardar ambas por compatibilidad
          estadoPago: estadoPagoTrans,
          colorBandera: colorBanderaTrans,
          consumoM3: item.consumoM3,
          precioM3: fileData?.precioM3 || 0,
          periodoLectura,
          adeudoNuevo: nuevoAdeudo,
          saldoFavorNuevo: nuevoSaldoFavor,
          observaciones: observacionesTrans,
          esPagoAguaPotable: true,
          createdAt: Timestamp.now()
        };
        await setDoc(refIngresoGlobal, transData);

        // 2. Registrar siempre el cargo en la subcolección mesesPendientes
        const refPendiente = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', item.casaId, 'mesesPendientes', transaccionId);
        await setDoc(refPendiente, {
          id: transaccionId,
          casaId: item.casaId,
          concepto: conceptoTrans,
          descripcion: conceptoTrans,
          tipo: 'adeudo_agua',
          referencia: refPeriodo,
          monto: cargoAgua,
          montoOriginal: cargoAgua,
          montoPendiente: pagadoCompleto ? 0 : cargoAgua,
          fecha: fechaTimestamp,
          fechaCorte: fechaTimestamp,
          estado: pagadoCompleto ? 'pagado' : 'pendiente',
          estadoResidente: pagadoCompleto ? 'AL CORRIENTE' : 'CON ADEUDO',
          colorBandera: pagadoCompleto ? 'verde' : 'rojo',
          fraccionamientoId
        });

        // Si fue pagado completo por saldo a favor, marcar en el desglose del período
        if (pagadoCompleto) {
          item.pagado = true;
          item.estadoPago = 'PAGADO';
          item.montoPendiente = 0;
        }
      }
    }
  };

  // Botón "Asignar a Historial de pagos" posterior con candado de 28 días
  const handleAsignarHistorialPosterior = async () => {
    if (!periodoSeleccionado) return;

    if (periodoSeleccionado.cargadoEnHistorial) {
      Swal.fire('Candado de Seguridad', 'Este período de consumo ya fue asignado previamente al historial de pagos de los residentes.', 'info');
      return;
    }

    // Regla de los 28 días: Verificar fecha del último período asignado
    const ultimoAsignado = historialConsumos.find((h) => h.cargadoEnHistorial === true && h.id !== periodoSeleccionado.id);
    if (ultimoAsignado && ultimoAsignado.fechaAsignacionHistorial) {
      const fechaUltimo = new Date(ultimoAsignado.fechaAsignacionHistorial);
      const diffDias = Math.floor((Date.now() - fechaUltimo.getTime()) / (1000 * 60 * 60 * 24));

      if (diffDias < 28 && ultimoAsignado.anoLectura === periodoSeleccionado.anoLectura) {
        const confirmSub = await Swal.fire({
          title: 'Aviso de Período Reciente (<28 días)',
          html: `El último período asignado al historial fue hace <b>${diffDias} días</b>.<br>¿Está seguro de que desea asignar este recibo retroactivo o adicional al historial de pagos?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sí, Asignar al Historial',
          cancelButtonText: 'Cancelar'
        });
        if (!confirmSub.isConfirmed) return;
      }
    }

    try {
      setIsProcessing(true);
      const docRef = doc(db, 'fraccionamientos', fraccionamientoId, 'consumosAguaPotable', periodoSeleccionado.id);
      const desglose = periodoSeleccionado.desgloseCasas || [];

      await aplicarCargosAHistorialResidentes(desglose, periodoSeleccionado.periodoLectura, periodoSeleccionado.mesLectura, periodoSeleccionado.anoLectura);

      await updateDoc(docRef, {
        cargadoEnHistorial: true,
        desgloseCasas: desglose,
        fechaAsignacionHistorial: new Date().toISOString()
      });

      Swal.fire('Asignación Completada', 'Se asignaron los cargos de agua al historial de cada residente sin duplicar registros.', 'success');
    } catch (err) {
      console.error('Error al asignar historial posterior:', err);
      Swal.fire('Error', 'No se pudo asignar el consumo al historial.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Botón "Revertir Consumo y Saldos" por error del administrador
  const handleRevertirConsumo = async () => {
    if (!periodoSeleccionado || !fraccionamientoId) return;

    // Candado de Seguridad de Calendario (Día 28) - DESACTIVADO TEMPORALMENTE PARA PRUEBAS
    // const diaActual = new Date().getDate();
    // if (diaActual > 28) {
    //   Swal.fire({
    //     title: 'Candado de Seguridad',
    //     text: 'No es posible revertir o eliminar el período de consumo de agua una vez transcurrido el día 28 de cada mes para proteger la consistencia de cierres históricos.',
    //     icon: 'warning'
    //   });
    //   return;
    // }

    const confirm = await Swal.fire({
      title: '⚠️ ¿Desea revertir este consumo y saldos?',
      html: `<div style="text-align: left; font-size: 0.95rem;">` +
        `Esta acción eliminará de forma permanente el recibo de consumo de agua de este período y restaurará los saldos de los residentes de la siguiente manera:<br><br>` +
        `• <b>Residentes con Saldo a Favor</b>: Se reintegrará de forma íntegra el importe cobrado a su saldo a favor disponible.<br><br>` +
        `• <b>Residentes Morosos (con Adeudo)</b>: Si el recibo fue liquidado manualmente, ese dinero se aplicará de forma automática para amortizar/reducir sus otros adeudos ordinarios pendientes, evitando la coexistencia de saldo a favor y adeudo.<br><br>` +
        `• <b>Cargos no pagados</b>: Se cancelará el cargo y se restará del adeudo actual de la vivienda.<br><br>` +
        `<span class="text-danger"><b>Esta acción eliminará los registros de pago y movimientos asociados y no se puede deshacer.</b></span>` +
        `</div>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, Revertir Saldos',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6c757d'
    });

    if (!confirm.isConfirmed) return;

    try {
      setIsProcessing(true);
      const desglose = periodoSeleccionado.desgloseCasas || [];
      const mesLectura = periodoSeleccionado.mesLectura || 'ACTUAL';
      const anoLectura = periodoSeleccionado.anoLectura || new Date().getFullYear();

      for (const item of desglose) {
        if (!item.casaId) continue;

        const transaccionId = `AGUA-${item.casaId}-${mesLectura}-${anoLectura}`;
        const ingresoRef = doc(db, 'fraccionamientos', fraccionamientoId, 'ingresos', transaccionId);
        
        let esPagoSaldoFavor = false;
        let transaccionExiste = false;

        try {
          const ingresoSnap = await getDoc(ingresoRef);
          if (ingresoSnap.exists()) {
            transaccionExiste = true;
            const ingData = ingresoSnap.data();
            const concepto = String(ingData.concepto || '').toLowerCase();
            const metodoPago = String(ingData.metodoPago || '').toLowerCase();
            const neto = Number(ingData.neto || 0);
            
            if (metodoPago === 'registro_automatico' || neto > 0 || concepto.includes('saldo a favor') || concepto.includes('sf')) {
              esPagoSaldoFavor = true;
            }
          }
        } catch (e) {
          console.warn('Error al verificar método de pago del ingreso en reversión:', e);
        }

        const casaRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', item.casaId);
        const casaSnap = await getDoc(casaRef);

        if (casaSnap.exists()) {
          const dataCasa = casaSnap.data();
          const adeudoActual = Number(dataCasa.adeudo || dataCasa.deudaTotal || 0);
          const saldoFavorActual = Number(dataCasa.saldoFavor || 0);
          const cargoAgua = Number(item.cargoTotal || 0);

          let nuevoAdeudo = adeudoActual;
          let nuevoSaldoFavor = saldoFavorActual;

          const estaPagado = item.estadoPago === 'PAGADO' || item.pagado === true;

          if (estaPagado) {
            if (esPagoSaldoFavor) {
              // Pago automático de Excel cubierto con Saldo a Favor -> se le reintegra completo al SF
              nuevoSaldoFavor = Math.round((saldoFavorActual + cargoAgua) * 100) / 100;
            } else {
              // Pago manual con dinero nuevo -> se amortiza contra su deuda para evitar saldo a favor duplicado
              if (adeudoActual > 0) {
                const amortizacion = Math.min(adeudoActual, cargoAgua);
                nuevoAdeudo = Math.max(0, Math.round((adeudoActual - amortizacion) * 100) / 100);
                const remanente = Math.round((cargoAgua - amortizacion) * 100) / 100;
                nuevoSaldoFavor = Math.round((saldoFavorActual + remanente) * 100) / 100;
              } else {
                nuevoSaldoFavor = Math.round((saldoFavorActual + cargoAgua) * 100) / 100;
              }
            }
          } else {
            // El cargo no se pagó -> se resta del adeudo total de la casa de forma segura
            nuevoAdeudo = Math.max(0, Math.round((adeudoActual - cargoAgua) * 100) / 100);
          }

          // Actualizar casa en Firestore
          await updateDoc(casaRef, {
            adeudo: nuevoAdeudo,
            saldoFavor: nuevoSaldoFavor,
            tieneAdeudoAguaVariable: false
          });
        }

        // Eliminar las transacciones deterministas creadas
        try {
          await deleteDoc(doc(db, 'fraccionamientos', fraccionamientoId, 'ingresos', transaccionId));
        } catch (e) {}
        try {
          await deleteDoc(doc(db, 'fraccionamientos', fraccionamientoId, 'casas', item.casaId, 'mesesPendientes', transaccionId));
        } catch (e) {}
      }

      // Eliminar el documento global de consumos de agua
      await deleteDoc(doc(db, 'fraccionamientos', fraccionamientoId, 'consumosAguaPotable', periodoSeleccionado.id));

      Swal.fire('Reversión Completada', 'El consumo y los saldos asociados han sido revertidos y eliminados correctamente.', 'success');
      setSelectedPeriodoId(null);
    } catch (err) {
      console.error('Error al revertir consumo de agua:', err);
      Swal.fire('Error', 'Ocurrió un error al revertir el consumo y los saldos.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Generar reporte en PDF del período de consumo de agua potable
  const generarReportePDFAgua = () => {
    if (!periodoSeleccionado) {
      Swal.fire('Atención', 'No hay un período de consumo de agua seleccionado para generar el reporte.', 'info');
      return;
    }

    const desglose = periodoSeleccionado.desgloseCasas || [];
    const totalCasas = desglose.length;
    let pagadas = 0;
    let totalRecaudado = 0;
    let totalCargo = 0;

    desglose.forEach((item) => {
      const cargo = Number(item.cargoTotal || 0);
      totalCargo += cargo;
      if (item.estadoPago === 'PAGADO' || item.pagado === true) {
        pagadas++;
        totalRecaudado += cargo;
      }
    });

    const pendientes = totalCasas - pagadas;
    const porcentajePagado = totalCasas > 0 ? Math.round((pagadas / totalCasas) * 100) : 0;
    const totalPendienteMonto = totalCargo - totalRecaudado;

    const configRecibos = fraccConfig?.configuracionRecibos || {};
    const condominioNombre = configRecibos.condominioNombre || fraccConfig?.condominioNombre || fraccConfig?.nombre || userData?.fraccionamientoNombre || 'Fraccionamiento';
    const logoUrl = configRecibos.logoUrl || fraccConfig?.logoUrl || userData?.fraccionamientoLogoUrl || userData?.logoUrl || null;
    const nowStr = new Date().toLocaleString('es-MX', { timeZone: 'America/Mexico_City', dateStyle: 'medium', timeStyle: 'medium' });

    const w = window.open('', '_blank');
    w.document.write(`
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8"/>
    <title>Reporte de Avance - Consumo Agua Potable (${periodoSeleccionado.periodoLectura})</title>
    <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #0f172a; background: #ffffff; padding: 32px; font-size: 12px; }
        .header-container { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #0284c7; padding-bottom: 16px; margin-bottom: 24px; }
        .header-left { display: flex; align-items: center; gap: 16px; }
        .logo-img { height: 60px; max-width: 180px; object-fit: contain; }
        .condo-name { font-size: 20px; font-weight: 800; color: #0369a1; text-transform: uppercase; letter-spacing: -0.02em; }
        .report-title { font-size: 13px; font-weight: 600; color: #475569; margin-top: 2px; }
        .timestamp-card { background: #f8fafc; border: 1px solid #cbd5e1; border-radius: 8px; padding: 8px 14px; text-align: right; }
        .ts-label { font-size: 8px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; }
        .ts-value { font-size: 11px; font-weight: 700; color: #0f172a; margin-top: 2px; }
        .ts-sub { font-size: 9px; color: #0284c7; font-weight: 600; margin-top: 1px; }
        
        .dashboard-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 16px; margin-bottom: 24px; align-items: center; }
        .metrics-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
        .metric-box { border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px; background: #f8fafc; text-align: center; }
        .metric-box .label { font-size: 9px; text-transform: uppercase; color: #64748b; font-weight: 700; letter-spacing: 0.03em; }
        .metric-box .val { font-size: 17px; font-weight: 800; margin-top: 4px; }
        .val.blue { color: #0284c7; }
        .val.green { color: #16a34a; }
        .val.red { color: #dc2626; }

        .chart-box { border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px; background: #ffffff; display: flex; align-items: center; justify-content: space-around; }
        .donut-chart { width: 80px; height: 80px; transform: rotate(-90deg); border-radius: 50%; }
        .chart-legend { font-size: 10px; }
        .legend-item { display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
        .dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
        .dot.green { background: #22c55e; }
        .dot.red { background: #ef4444; }

        h2 { font-size: 13px; font-weight: 700; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 1px solid #e2e8f0; color: #0f172a; text-transform: uppercase; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 8px; }
        th { text-align: left; padding: 8px 10px; background: #0f172a; color: #ffffff; font-weight: 700; font-size: 9px; text-transform: uppercase; }
        td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; }
        tr:nth-child(even) td { background-color: #f8fafc; }
        .badge { display: inline-block; padding: 3px 8px; border-radius: 99px; font-size: 9px; font-weight: 800; letter-spacing: 0.03em; }
        .badge-green { background: #d1fae5; color: #065f46; }
        .badge-red { background: #fee2e2; color: #991b1b; }
        .text-right { text-align: right; }
        .footer { margin-top: 32px; text-align: center; color: #94a3b8; font-size: 10px; border-top: 1px solid #e2e8f0; padding-top: 12px; }
        @media print { body { padding: 16px; } }
    </style>
</head>
<body>
    <div class="header-container">
        <div class="header-left">
            ${logoUrl ? `<img src="${logoUrl}" alt="Logo ${condominioNombre}" class="logo-img" />` : ''}
            <div>
                <div class="condo-name">${condominioNombre}</div>
                <div class="report-title">Reporte de Avance - Consumo Agua Potable (${periodoSeleccionado.periodoLectura})</div>
            </div>
        </div>
        <div class="timestamp-card">
            <div class="ts-label">Fecha y Hora de Descarga</div>
            <div class="ts-value">${nowStr}</div>
            <div class="ts-sub">Tiempo del Centro de México (CST)</div>
        </div>
    </div>

    <div class="dashboard-grid">
        <div class="metrics-row">
            <div class="metric-box">
                <div class="label">Monto Total a Recaudar</div>
                <div class="val blue">${fmtMXN(totalCargo)}</div>
            </div>
            <div class="metric-box">
                <div class="label">Recaudación Alcanzada</div>
                <div class="val green">${fmtMXN(totalRecaudado)}</div>
            </div>
            <div class="metric-box">
                <div class="label">Pendiente por Cobrar</div>
                <div class="val red">${fmtMXN(totalPendienteMonto)}</div>
            </div>
        </div>
        <div class="chart-box">
            <svg class="donut-chart" viewBox="0 0 36 36">
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#fee2e2" stroke-width="4.5" />
                <path stroke-dasharray="${porcentajePagado}, 100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#16a34a" stroke-width="4.5" />
            </svg>
            <div class="chart-legend">
                <div style="font-weight: 800; font-size: 14px; color: #0f172a; margin-bottom: 4px;">${porcentajePagado}% Recaudado</div>
                <div class="legend-item"><span class="dot green"></span> <strong>${pagadas}</strong> Casas al corriente (${fmtMXN(totalRecaudado)})</div>
                <div class="legend-item"><span class="dot red"></span> <strong>${pendientes}</strong> Casas pendientes (${fmtMXN(totalPendienteMonto)})</div>
            </div>
        </div>
    </div>

    <h2>Detalle de Consumo y Estatus por Residente</h2>
    <table>
        <thead>
            <tr>
                <th>Casa</th>
                <th>Lectura Anterior</th>
                <th>Lectura Actual</th>
                <th>Consumo (m³)</th>
                <th class="text-right">Importe</th>
                <th class="text-right">Prorrateo</th>
                <th class="text-right">Cargo Total</th>
                <th style="text-align: center;">Estatus</th>
            </tr>
        </thead>
        <tbody>
            ${desglose.map(item => {
              const isPaid = item.pagado || item.estadoPago === 'PAGADO';
              return `
                <tr>
                    <td><strong>Casa ${item.casaNumero}</strong></td>
                    <td>${Number(item.lecturaAnterior || 0).toFixed(2)} m³</td>
                    <td>${Number(item.lecturaActual || 0).toFixed(2)} m³</td>
                    <td><strong>${Number(item.consumoM3 || 0).toFixed(2)} m³</strong></td>
                    <td class="text-right">${fmtMXN(item.consumoPesos)}</td>
                    <td class="text-right">${fmtMXN(item.prorrateoComun)}</td>
                    <td class="text-right"><strong>${fmtMXN(item.cargoTotal)}</strong></td>
                    <td style="text-align: center;">
                        <span class="badge ${isPaid ? 'badge-green' : 'badge-red'}">
                            ${isPaid ? 'PAGADO' : 'PENDIENTE'}
                        </span>
                    </td>
                </tr>
              `;
            }).join('')}
            <tr style="font-weight: 700; background-color: #f1f5f9;">
                <td colspan="4">TOTALES DEL PERÍODO</td>
                <td class="text-right">${fmtMXN(desglose.reduce((s, i) => s + Number(i.consumoPesos || 0), 0))}</td>
                <td class="text-right">${fmtMXN(desglose.reduce((s, i) => s + Number(i.prorrateoComun || 0), 0))}</td>
                <td class="text-right" style="color: #0284c7;">${fmtMXN(totalCargo)}</td>
                <td style="text-align: center;">${pagadas} / ${totalCasas} Pagados</td>
            </tr>
        </tbody>
    </table>

    <div class="footer">
        Reporte oficial de avance de pagos de consumo de agua potable generado por Domus Hábitat para ${condominioNombre}.
    </div>

    <script>
        window.onload = () => setTimeout(() => window.print(), 600);
    </script>
</body>
</html>`);
    w.document.close();
  };

  return (
    <div className="consumo-agua-page">
      {/* Encabezado Principal Envolvente con Contraste */}
      <header className="agua-header">
        <div className="agua-header-main">
          <div className="agua-header-icon">
            <FaWater />
          </div>
          <div className="agua-header-title-block">
            <h1>Consumo - Agua Potable</h1>
            <p>Lectura, validación y gestión del cobro variable de agua por residente</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn-agua-pdf-header"
            onClick={generarReportePDFAgua}
            disabled={!periodoSeleccionado}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              borderRadius: '99px',
              background: '#ffffff',
              border: '1.5px solid #0284c7',
              color: '#0369a1',
              fontSize: '13px',
              fontWeight: '600',
              cursor: !periodoSeleccionado ? 'not-allowed' : 'pointer',
              opacity: !periodoSeleccionado ? 0.6 : 1,
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
            }}
          >
            <FaFilePdf /> Reporte PDF
          </button>
          <button className="btn-regresar-admin" onClick={() => navigate('/administracion')}>
            <FaArrowLeft /> Regresar a administración
          </button>
        </div>
      </header>

      {/* Banner de Ayuda y Guía Informativa (Punto 9) */}
      <div className="agua-help-card">
        <div className="help-icon">
          <FaInfoCircle />
        </div>
        <div className="help-content">
          <h4>Guía de Uso y Advertencia para Administradores (Cajas Separadas Activado)</h4>
          <p>
            Esta sección permite cargar el archivo Excel o PDF con el consumo en m³ por residente para el proveedor de agua potable.
            Al subir el documento, el sistema verificará errores de lectura y fechas duplicadas.
            Al confirmar la lectura, podrá elegir si desea marcar inmediatamente los cargos en el historial financiero de cada residente o conservarlo únicamente como registro informativo de la administración.
            <br />
            <strong>Nota: El saldo a favor de mantenimiento de los residentes al corriente se mantendrá intacto y la cuota de agua quedará pendiente para cobro manual.</strong>
          </p>
        </div>
      </div>

      {/* Tarjetas de Acciones Principales: Descargar Plantilla & Cargar Archivo */}
      <div className="agua-actions-grid">
        <div className="agua-action-card">
          <div className="action-icon-circle action-blue">
            <FaDownload />
          </div>
          <div className="action-details">
            <h3>1. Descargar Plantilla</h3>
            <p>Obtenga el formato Excel idóneo para ingresar las lecturas de los residentes.</p>
            <button className="btn-agua-secondary" onClick={descargarPlantillaAgua}>
              <FaFileExcel /> Descargar Plantilla .xlsx
            </button>
          </div>
        </div>

        <div className="agua-action-card">
          <div className="action-icon-circle action-green">
            <FaUpload />
          </div>
          <div className="action-details">
            <h3>2. Cargar Recibo / Lecturas</h3>
            <p>Suba el archivo Excel o PDF de consumos del mes para realizar la lectura automática.</p>
            <label className="btn-agua-primary label-file-upload">
              {fileLoading ? 'Procesando archivo...' : 'Seleccionar o Arrastrar Archivo'}
              <input
                type="file"
                accept=".xlsx, .xls, .pdf"
                onChange={handleFileUpload}
                disabled={fileLoading}
                style={{ display: 'none' }}
              />
            </label>
          </div>
        </div>
      </div>

      {/* Selector de Período y Estadísticas de Porcentaje (Punto 7) */}
      {historialConsumos.length > 0 && (
        <div className="agua-period-selector-card">
          <div className="selector-header">
            <label><strong>Seleccionar Período de Consumo:</strong></label>
            <select
              className="agua-select"
              value={selectedPeriodoId || ''}
              onChange={(e) => setSelectedPeriodoId(e.target.value)}
            >
              {historialConsumos.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.periodoLectura} ({h.mesLectura} {h.anoLectura}) - Total Recibo: {fmtMXN(h.totalReciboCEA)} {h.cargadoEnHistorial ? '✓ [En Historial]' : 'ⓘ [Informativo]'}
                </option>
              ))}
            </select>
          </div>

          {/* Tarjetas de Métrica / Porcentaje de Cobro */}
          <div className="agua-metrics-grid">
            <div className="agua-metric-card">
              <span className="metric-title">Adeudo Total al Proveedor</span>
              <span className="metric-value text-blue">{fmtMXN(metricasPeriodo.totalCEA)}</span>
              <span className="metric-sub">Factura total del fraccionamiento</span>
            </div>

            <div className="agua-metric-card">
              <span className="metric-title">Recaudación Alcanzada</span>
              <span className="metric-value text-green">{fmtMXN(metricasPeriodo.totalRecaudado)}</span>
              <span className="metric-sub">De residentes que ya cubrieron cuota</span>
            </div>

            <div className="agua-metric-card">
              <span className="metric-title">Estatus de Pagos Residentes</span>
              <div className="metric-progress-wrapper">
                <span className="metric-value">{metricasPeriodo.porcentajePagado}% Pagado</span>
                <div className="progress-bar-bg">
                  <div className="progress-bar-fill" style={{ width: `${metricasPeriodo.porcentajePagado}%` }}></div>
                </div>
              </div>
              <span className="metric-sub">
                {metricasPeriodo.pagadas} Pagados • {metricasPeriodo.pendientes} Pendientes
              </span>
            </div>

            <div className="agua-metric-card">
              <span className="metric-title">Estado en Historial</span>
              <span className={`status-badge ${periodoSeleccionado?.cargadoEnHistorial ? 'badge-success' : 'badge-warning'}`}>
                {periodoSeleccionado?.cargadoEnHistorial ? 'Cargado en Historial' : 'Solo Informativo'}
              </span>
              {!periodoSeleccionado?.cargadoEnHistorial && (
                <button
                  className="btn-agua-small-assign"
                  onClick={handleAsignarHistorialPosterior}
                  disabled={isProcessing}
                >
                  <FaHistory /> Asignar a Historial de Pagos
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tabla Desglose de Residentes por Período (Punto 7) */}
      {periodoSeleccionado && (
        <div className="agua-table-container">
          <div className="table-header-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h3>Detalle de Consumo por Residente - {periodoSeleccionado.periodoLectura}</h3>
              <span className="precio-m3-badge">Precio m³: ${Number(periodoSeleccionado.precioM3 || 0).toFixed(2)}</span>
            </div>
            {modulos?.revertir_consumo_agua === true && (
              <button
                type="button"
                className="btn-agua-revert"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  transition: 'all 0.2s'
                }}
                onClick={handleRevertirConsumo}
                disabled={isProcessing}
              >
                <FaTimesCircle /> Revertir Consumo y Saldos
              </button>
            )}
          </div>

          <table className="agua-table">
            <thead>
              <tr>
                <th>Casa / Residente</th>
                <th>Lectura Anterior (m³)</th>
                <th>Lectura Actual (m³)</th>
                <th>Consumo (m³)</th>
                <th>Importe Consumo</th>
                <th>Prorrateo Común</th>
                <th>Cargo Total</th>
                <th>Estatus de Pago</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const totalCasasPeriodo = (periodoSeleccionado.desgloseCasas || []).length;
                const itemsPerPageTable = 10;
                const totalPagesTable = Math.max(1, Math.ceil(totalCasasPeriodo / itemsPerPageTable));
                const startIndexTable = (currentPageTable - 1) * itemsPerPageTable;
                const paginatedDesglose = (periodoSeleccionado.desgloseCasas || []).slice(startIndexTable, startIndexTable + itemsPerPageTable);

                return paginatedDesglose.map((item, idx) => (
                  <tr key={idx}>
                    <td><strong>Casa {item.casaNumero}</strong></td>
                    <td>{Number(item.lecturaAnterior || 0).toFixed(2)} m³</td>
                    <td>{Number(item.lecturaActual || 0).toFixed(2)} m³</td>
                    <td><span className="badge-m3">{Number(item.consumoM3 || 0).toFixed(2)} m³</span></td>
                    <td>{fmtMXNEntero(item.consumoPesos)}</td>
                    <td>{fmtMXNEntero(item.prorrateoComun)}</td>
                    <td><strong>{fmtMXNEntero(item.cargoTotal)}</strong></td>
                    <td>
                      {item.pagado || item.estadoPago === 'PAGADO' ? (
                        <span className="tag-pagado"><FaCheckCircle /> PAGADO</span>
                      ) : (
                        <span className="tag-pendiente"><FaExclamationTriangle /> PENDIENTE</span>
                      )}
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>

          {/* Bar de Paginación */}
          {(() => {
            const totalCasasPeriodo = (periodoSeleccionado.desgloseCasas || []).length;
            const itemsPerPageTable = 10;
            const totalPagesTable = Math.max(1, Math.ceil(totalCasasPeriodo / itemsPerPageTable));
            const startIndexTable = (currentPageTable - 1) * itemsPerPageTable;
            if (totalCasasPeriodo === 0) return null;

            return (
              <div className="table-pagination-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                  Mostrando <strong>{startIndexTable + 1}</strong> a <strong>{Math.min(startIndexTable + itemsPerPageTable, totalCasasPeriodo)}</strong> de <strong>{totalCasasPeriodo}</strong> domicilios
                </span>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    type="button"
                    className="agua-pagination-btn"
                    disabled={currentPageTable <= 1}
                    onClick={() => setCurrentPageTable((prev) => Math.max(prev - 1, 1))}
                    style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', background: currentPageTable <= 1 ? '#f1f5f9' : '#ffffff', cursor: currentPageTable <= 1 ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: '600' }}
                  >
                    Anterior
                  </button>
                  <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155' }}>
                    Página {currentPageTable} de {totalPagesTable}
                  </span>
                  <button
                    type="button"
                    className="agua-pagination-btn"
                    disabled={currentPageTable >= totalPagesTable}
                    onClick={() => setCurrentPageTable((prev) => Math.min(prev + 1, totalPagesTable))}
                    style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', background: currentPageTable >= totalPagesTable ? '#f1f5f9' : '#ffffff', cursor: currentPageTable >= totalPagesTable ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: '600' }}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── MODAL 1: PREVISUALIZACIÓN Y LECTURA DE ERRORES (Punto 3) ── */}
      {showPreviewModal && fileData && (
        <div className="agua-modal-overlay">
          <div className="agua-modal-container modal-large">
            <div className="agua-modal-header">
              <h2><FaTint /> Previsualización: Consumo - Agua Potable</h2>
              <button className="agua-modal-close" onClick={() => setShowPreviewModal(false)}>✕</button>
            </div>

            <div className="agua-modal-body">
              {/* Reporte de Errores */}
              {erroresLectura.length > 0 ? (
                <div className="agua-error-banner">
                  <h4><FaTimesCircle /> Se detectaron los siguientes errores en la lectura:</h4>
                  <ul>
                    {erroresLectura.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                  <p className="error-note">Corrija el archivo Excel y vuelva a subirlo para poder continuar.</p>
                </div>
              ) : (
                <div className="agua-success-banner">
                  <FaCheckCircle /> Lectura completada sin errores. Verifique los totales antes de procesar.
                </div>
              )}

              {/* Resumen Global Parseado con campos editables de verificación */}
              <div className="preview-summary-grid">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#475569' }}>
                    Total Recibo CEA / Factura ($):
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={fileData.totalReciboCEA || ''}
                    onChange={(e) => handleUpdateTotalRecibo(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontWeight: '700',
                      color: '#0f172a',
                      background: '#ffffff'
                    }}
                    placeholder="0.00"
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#475569' }}>
                    Metros Cúbicos Totales:
                  </label>
                  <div style={{ padding: '8px 0', fontSize: '1.05rem', fontWeight: '700', color: '#0284c7' }}>
                    {Number(fileData.totalM3Fracc || 0).toFixed(2)} m³
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#475569' }}>
                    Precio por m³ ($):
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={fileData.precioM3 || ''}
                    onChange={(e) => handleUpdatePrecioM3(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontWeight: '700',
                      color: '#0f172a',
                      background: '#ffffff'
                    }}
                    placeholder="0.00"
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#475569' }}>
                    Período Detectado:
                  </label>
                  <input
                    type="text"
                    value={fileData.periodoLectura || ''}
                    onChange={(e) => handleUpdatePeriodo(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      color: '#0f172a',
                      background: '#ffffff'
                    }}
                    placeholder="Ej: 11 de agosto a 14 de septiembre"
                  />
                </div>
              </div>

              {/* Tabla de Previsualización por Casa */}
              <div className="preview-table-wrapper">
                <table className="agua-table modal-table">
                  <thead>
                    <tr>
                      <th>Casa</th>
                      <th>Lect. Anterior</th>
                      <th>Lect. Actual</th>
                      <th>Consumo (m³)</th>
                      <th>Consumo ($)</th>
                      <th>Prorrateo</th>
                      <th>Cargo Total ($)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fileData.casas.map((item, idx) => (
                      <tr key={idx}>
                        <td>Casa {item.casaNumero}</td>
                        <td>{Number(item.lecturaAnterior || 0).toFixed(2)} m³</td>
                        <td>{Number(item.lecturaActual || 0).toFixed(2)} m³</td>
                        <td>{Number(item.consumoM3 || 0).toFixed(2)} m³</td>
                        <td>{fmtMXNEntero(item.consumoPesos)}</td>
                        <td>{fmtMXNEntero(item.prorrateoComun)}</td>
                        <td><strong>{fmtMXNEntero(item.cargoTotal)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="agua-modal-footer">
              <button className="btn-agua-cancel" onClick={() => setShowPreviewModal(false)}>
                Cancelar
              </button>
              <button
                className="btn-agua-save"
                onClick={handleAprobarPrevisualizacion}
                disabled={erroresLectura.length > 0}
              >
                Procesar y Continuar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL 2: ADVERTENCIA Y PREGUNTA DE CONFIRMACIÓN SÍ / NO (Punto 4) ── */}
      {showConfirmModal && fileData && (
        <div className="agua-modal-overlay">
          <div className="agua-modal-container modal-medium">
            <div className="agua-modal-header header-warning">
              <h2><FaExclamationTriangle /> Advertencia de Asignación Financiera</h2>
            </div>

            <div className="agua-modal-body text-center">
              <div className="warning-big-icon">
                <FaShieldAlt />
              </div>
              <h3 className="confirm-title">
                "El consumo cargado se marcará en el historial del residente"
              </h3>
              <p className="confirm-subtitle">
                ¿Desea agregar este registro de consumo de agua directamente al historial de pagos y adeudos de cada residente?
              </p>

              <div className="confirm-rules-box">
                <p><strong>Reglas que se aplicarán al marcar SÍ:</strong></p>
                <ul>
                  <li>Se asignará con la fecha correspondiente al recibo cargado (<b>{fileData.periodoLectura}</b>).</li>
                  <li>Si la casa tiene adeudo, se sumará el monto de agua a su adeudo total.</li>
                  <li>Si la casa tiene saldo a favor, este se mantendrá intacto (cajas separadas) y la cuota de agua quedará pendiente de pago.</li>
                </ul>
              </div>
            </div>

            <div className="agua-modal-footer confirm-footer">
              <button className="btn-confirm-no" onClick={() => handleRespuestaConfirmacion(false)}>
                No (Solo Informativo)
              </button>
              <button className="btn-confirm-yes" onClick={() => handleRespuestaConfirmacion(true)}>
                Sí, Agregar al Historial
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsumoAguaPotable;
